/**
 * Dealer Service
 * Handles dealer management API calls for the simplified workflow
 */

import { api } from '../lib/axios';

export interface Dealer {
  id: string;
  name: string;
  businessName?: string;
  mobileNumber: string;
  address: string;
  email?: string;
  gstNumber?: string;
  contactPerson?: string;
  status: 'active' | 'inactive' | 'suspended';
  createdAt: string;
  updatedAt: string;
  packageCount?: number;
  subDealerCount?: number;
}

export interface SubDealer {
  id: string;
  name: string;
  mobileNumber: string;
  address: string;
  status: 'active' | 'inactive' | 'suspended';
  createdAt: string;
}

export interface DealerData {
  name: string;
  businessName?: string;
  mobileNumber: string;
  address: string;
  email?: string;
  gstNumber?: string;
  contactPerson?: string;
}

export interface SubDealerData {
  name: string;
  mobileNumber: string;
  address: string;
}

export interface DealerStats {
  totalDealers: number;
  totalSubDealers: number;
  activeAssignments: number;
  dealersByStatus: Record<string, number>;
}

class DealerService {
  /**
   * Add a new dealer to manufacturer's network
   */
  async addDealer(dealerData: DealerData): Promise<{ success: boolean; dealer?: Dealer; error?: string }> {
    try {
      const response = await api.post('/dealers', dealerData);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to add dealer'
      };
    }
  }

  /**
   * Get all dealers for manufacturer
   */
  async getDealerNetwork(): Promise<{ success: boolean; dealers?: Dealer[]; error?: string }> {
    try {
      const response = await api.get('/dealers');
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get dealer network'
      };
    }
  }

  /**
   * Get dealer by ID
   */
  async getDealerById(dealerId: string): Promise<{ success: boolean; dealer?: Dealer; error?: string }> {
    try {
      const response = await api.get(`/dealers/${dealerId}`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get dealer'
      };
    }
  }

  /**
   * Update dealer information
   */
  async updateDealer(dealerId: string, updates: Partial<DealerData>): Promise<{ success: boolean; dealer?: Dealer; error?: string }> {
    try {
      const response = await api.put(`/dealers/${dealerId}`, updates);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update dealer'
      };
    }
  }

  /**
   * Update dealer status
   */
  async updateDealerStatus(dealerId: string, status: string): Promise<{ success: boolean; dealer?: Dealer; error?: string }> {
    try {
      const response = await api.put(`/dealers/${dealerId}/status`, { status });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update dealer status'
      };
    }
  }

  /**
   * Remove dealer from network
   */
  async removeDealer(dealerId: string): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const response = await api.delete(`/dealers/${dealerId}`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to remove dealer'
      };
    }
  }

  /**
   * Add sub-dealer to dealer's network
   */
  async addSubDealer(dealerId: string, subDealerData: SubDealerData): Promise<{ success: boolean; subDealer?: SubDealer; error?: string }> {
    try {
      const response = await api.post(`/dealers/${dealerId}/sub-dealers`, subDealerData);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to add sub-dealer'
      };
    }
  }

  /**
   * Get sub-dealers for a dealer
   */
  async getSubDealers(dealerId: string): Promise<{ success: boolean; subDealers?: SubDealer[]; error?: string }> {
    try {
      const response = await api.get(`/dealers/${dealerId}/sub-dealers`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get sub-dealers'
      };
    }
  }

  /**
   * Get dealer statistics
   */
  async getDealerStats(): Promise<{ success: boolean; stats?: DealerStats; error?: string }> {
    try {
      const response = await api.get('/dealers/stats');
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get dealer statistics'
      };
    }
  }

  /**
   * Get dealer by mobile number (for OTP verification)
   */
  async getDealerByMobile(mobileNumber: string): Promise<{ success: boolean; dealer?: Dealer; error?: string }> {
    try {
      const response = await api.get(`/dealers/mobile/${mobileNumber}`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to find dealer'
      };
    }
  }
}

export const dealerService = new DealerService();